import { autorun, makeAutoObservable, runInAction, observable } from "mobx";
import { v4 as uuidv4 } from "uuid";

const initialState = {
    currentuserinput: "",
    messages: observable.map(),
    isFetching: false
};

class AiChatBotStore {
    rootStore;
    commonStateStore;
    uuid;
    selectedCheckboxes = observable.map();
    constructor(rootStore) {
        this.rootStore = rootStore;
        this.commonStateStore = new Map(Object.entries(initialState));
        this.uuid = uuidv4();
        makeAutoObservable(this);
    }

    applyTheValue(updates) {
        runInAction(() => {
            for (const [key, value] of Object.entries(updates)) {
                this.commonStateStore.set(key, value);
            }
        });
    }

    getValue = (key) => {
        return this.commonStateStore.get(key);
    }

    sendMessage = async () => {
        const messages = this.getValue("messages");
        const messageText = this.commonStateStore.get("currentuserinput")?.trim();

        if (!messageText) return;

        // Save sent message
        runInAction(() => {
            messages.set(uuidv4(), {
                type: "sent",
                data: { text: messageText },
            });
            this.commonStateStore.set("messages", messages);
            this.commonStateStore.set("currentuserinput", "");
        });

        this.applyTheValue({ isFetching: true });

        let response;
        try {
            response = await this.rootStore.commonAxios(
                "/chatbot",
                "POST",
                {},
                {
                    tableId: this.rootStore.gridStore.tabIndex === 0
                        ? this.rootStore.gridStore.tableCompanyId
                        : this.rootStore.gridStore.tablePeopleId,
                    uuid: this.uuid,
                    textSearch: messageText,
                }
            );
        } catch (error) {
            console.error("Chatbot API error:", error);
        } finally {
            this.applyTheValue({ isFetching: false });
        }

        console.log(response, "Got in data for response sent");

        if (response?.statusCode === 200) {
            this.receiveMessage({ body: response.data.reply });
        }
    };

    receiveMessage = (payload) => {
        const messages = this.getValue("messages");
        runInAction(() => {
            messages.set(uuidv4(), {
                type: "receive",
                data: payload,
            });
            this.commonStateStore.set("messages", messages);
        });
    };

    clearMe() {
        runInAction(() => {
            this.commonStateStore.set("messages", new Map());
            this.commonStateStore.set("currentuserinput", "");
            this.commonStateStore.set("isFetching", false);
            this.uuid = uuidv4();
            this.selectedCheckboxes.clear();
            this.rootStore?.clearAll();
        });
    }

    getSelectedCheckboxes = (uuid) => {
        return this.selectedCheckboxes.get(uuid) || new Set();
    };

    handleChatCheckboxClick = (uuid, label) => {
        runInAction(() => {
            const selected = this.selectedCheckboxes.get(uuid) || new Set();
            if (selected.has(label)) {
                selected.delete(label);
            } else {
                selected.add(label);
            }
            this.selectedCheckboxes.set(uuid, new Set(selected));
        });

    };
    parseTextToAccordionStructure = (text) => {
        const lines = text.split('\n');
        const result = [];
        let currentSection = null;
        let anonymousAccordionBuffer = [];

        for (let rawLine of lines) {
            const line = rawLine.trim();
            if (!line) continue;

            // Check for **Title:**
            const titleMatch = line.match(/^\s*\*\*(.+?):\*\*\s*$/);
            if (titleMatch) {
                if (anonymousAccordionBuffer.length > 0) {
                    result.push({
                        type: "accordion",
                        title: "Options",
                        options: anonymousAccordionBuffer.map(option => ({ label: option, value: option }))
                    });
                    anonymousAccordionBuffer = [];
                }

                if (currentSection) {
                    result.push(currentSection);
                }

                currentSection = {
                    type: "accordion",
                    title: titleMatch[1].trim(),
                    options: []
                };
                continue;
            }

            // Match * Line
            const optionMatch = line.match(/^\*\s+(.+)/);
            if (optionMatch) {
                const option = optionMatch[1].trim();
                if (currentSection) {
                    currentSection.options.push({ label: option, value: option });
                } else {
                    anonymousAccordionBuffer.push(option);
                }
                continue;
            }

            // If plain text, flush open section
            if (currentSection) {
                result.push(currentSection);
                currentSection = null;
            }

            if (anonymousAccordionBuffer.length > 0) {
                result.push({
                    type: "accordion",
                    title: "Options",
                    options: anonymousAccordionBuffer.map(option => ({ label: option, value: option }))
                });
                anonymousAccordionBuffer = [];
            }

            // Detect **Label: ** Content OR Label: Content
            const labeledMatch = line.match(/^(?:\*\*)?(.+?):(?:\*\*)?\s*(.+)$/);
            if (labeledMatch) {
                result.push({
                    type: "text",
                    title: labeledMatch[1].trim(),
                    content: labeledMatch[2].trim()
                });
            } else {
                result.push({
                    type: "text",
                    content: line
                });
            }
        }

        if (currentSection) {
            result.push(currentSection);
        }

        if (anonymousAccordionBuffer.length > 0) {
            result.push({
                type: "accordion",
                title: "Options",
                options: anonymousAccordionBuffer.map(option => ({ label: option, value: option }))
            });
        }

        return result;
    };

    handleCheckboxClick = (data) => {
        const currentInput = this.getValue("currentuserinput") || "";

        // Convert current input to a Set for easy add/remove logic
        const inputSet = new Set(
            currentInput
                .split(',')
                .map(str => str.trim())
                .filter(Boolean)
        );

        if (inputSet.has(data)) {
            inputSet.delete(data); // Unchecked: remove it
        } else {
            inputSet.add(data); // Checked: add it
        }

        // Convert set back to string
        const updatedInput = Array.from(inputSet).join(', ');

        runInAction(() => {
            this.commonStateStore.set("currentuserinput", updatedInput);
        });
    };
    wasOptionEverSelected = (valueToCheck) => {
        const messages = this.getValue("messages");
        const currentInput = this.getValue("currentuserinput") || "";

        // Check all sent messages
        for (let [, msg] of messages) {
            if (msg?.type === "sent") {
                const text = msg?.data?.text || msg?.data?.body || "";
                if (typeof text === "string" && text.includes(valueToCheck)) {
                    return true;
                }
            }
        }

        // Check current user input
        if (typeof currentInput === "string" && currentInput.includes(valueToCheck)) {
            return true;
        }

        return false;
    };


}

export default AiChatBotStore;
